class RealVideoFeature extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-real-video-stage]');
    this.shell = this.querySelector('[data-real-video-shell]');
    this.guide = this.querySelector('[data-real-video-guide]');
    this.details = this.querySelector('[data-real-video-details]');
    this.media = this.querySelector('[data-real-video-media]');
    this.posterButton = this.querySelector('[data-real-video-play]');
    this.posterImage = this.querySelector('[data-youtube-poster]');
    this.player = this.querySelector('[data-real-video-player]');
    this.playerTemplate = this.querySelector('template[data-real-video-template]');
    this.chapterButtons = [...this.querySelectorAll('[data-real-video-chapter]')];

    if (!this.stage || !this.shell || !this.details || !this.media) return;

    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.designMode = this.dataset.designMode === 'true';
    this.animationRequested = this.dataset.animation === 'true';
    this.animationStart = (Number(this.dataset.animationStart) || 85) / 100;
    this.animationEnd = (Number(this.dataset.animationEnd) || 50) / 100;
    this.completionProgress = (Number(this.dataset.completionProgress) || 100) / 100;
    this.scrubDuration = Number(this.dataset.scrubDuration) || 350;
    this.autoplayRequested = this.dataset.autoplay === 'true';
    this.pauseOffscreen = this.dataset.pauseOffscreen === 'true';
    this.maxInnerWidth = this.guide?.getBoundingClientRect().width || this.clientWidth;
    this.animationReferenceHeight = this.calculateAnimationReferenceHeight();
    this.currentProgress = this.animationEnabled ? this.calculateProgress() : 1;
    this.targetProgress = this.currentProgress;
    this.isIntersecting = false;
    this.visibilityRatio = 0;
    this.autoStarted = false;
    this.frame = null;
    this.resizeFrame = null;
    this.lastFrameTime = null;
    this.stableFrames = 0;
    this.contentVisible = null;
    this.isCollapsed = null;
    this.pendingSeek = null;
    this.playerReady = false;

    this.posterButton?.addEventListener('click', this.handlePlayClick);
    this.posterImage?.addEventListener('error', this.handlePosterError);
    this.addEventListener('click', this.handleChapterClick);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    document.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
    window.visualViewport?.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.addMediaQueryListener(this.mobileQuery, this.handleResize);
    this.addMediaQueryListener(this.reducedMotionQuery, this.handleMotionChange);
    document.addEventListener('shopify:section:select', this.handleSectionSelect);
    document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.prepareChapters();
    this.observeVisibility();
    this.refreshLayout();
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.posterButton?.removeEventListener('click', this.handlePlayClick);
    this.posterImage?.removeEventListener('error', this.handlePosterError);
    this.removeEventListener('click', this.handleChapterClick);
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('scroll', this.handleScroll, { capture: true });
    window.visualViewport?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.removeMediaQueryListener(this.mobileQuery, this.handleResize);
    this.removeMediaQueryListener(this.reducedMotionQuery, this.handleMotionChange);
    document.removeEventListener('shopify:section:select', this.handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.visibilityObserver?.disconnect();

    if (this.frame) window.cancelAnimationFrame(this.frame);
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
  }

  get animationEnabled() {
    return (
      this.animationRequested &&
      !this.mobileQuery?.matches &&
      !this.reducedMotionQuery?.matches &&
      !this.editorOverride
    );
  }

  get autoplayEnabled() {
    return this.autoplayRequested && !this.designMode && !this.reducedMotionQuery?.matches;
  }

  get expansionComplete() {
    return !this.animationEnabled || (this.targetProgress >= 0.999 && this.currentProgress >= 0.995);
  }

  get autoplayVisibilityReached() {
    return !this.mobileQuery?.matches || this.visibilityRatio >= 0.6;
  }

  handleScroll = () => {
    if (!this.animationEnabled) return;

    this.stableFrames = 0;
    this.targetProgress = this.calculateProgress();
    this.requestTick();
  };

  handleResize = () => {
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.refreshLayout({ syncProgress: true });
    });
  };

  handleMotionChange = () => {
    this.targetProgress = this.animationEnabled ? this.calculateProgress() : 1;
    this.currentProgress = this.targetProgress;
    this.refreshLayout();
  };

  handlePlayClick = () => {
    this.loadAndPlay();
  };

  handleChapterClick = (event) => {
    const button = event.target.closest?.('[data-real-video-chapter]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    const seconds = Number(button.dataset.seconds);
    if (!Number.isFinite(seconds)) return;

    this.loadAndPlay(seconds);
  };

  handlePosterError = () => {
    if (!(this.posterImage instanceof HTMLImageElement)) return;

    const fallback = this.posterImage.dataset.fallbackSrc;
    if (!fallback || this.posterImage.src === fallback) return;

    this.posterImage.src = fallback;
  };

  handleSectionSelect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;

    this.editorOverride = true;
    this.currentProgress = 1;
    this.targetProgress = 1;
    this.refreshLayout();
  };

  handleSectionDeselect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;

    this.editorOverride = false;
    this.targetProgress = this.animationEnabled ? this.calculateProgress() : 1;
    this.currentProgress = this.targetProgress;
    this.refreshLayout();
  };

  handleBlockSelect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;

    const selectedItem = [...this.querySelectorAll('[data-real-video-chapter-item]')].find(
      (item) => item.dataset.blockId === event.detail?.blockId
    );
    if (!(selectedItem instanceof HTMLElement)) return;

    this.editorOverride = true;
    this.currentProgress = 1;
    this.targetProgress = 1;
    this.refreshLayout();
    selectedItem.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  };

  prepareChapters() {
    for (const button of this.chapterButtons) {
      const seconds = this.parseTimecode(button.dataset.timecode || '');

      if (seconds === null) {
        button.disabled = true;
        button.title = 'Enter the time as seconds, MM:SS or H:MM:SS.';
        continue;
      }

      button.dataset.seconds = String(seconds);
    }
  }

  parseTimecode(value) {
    const timecode = value.trim();
    if (!/^\d+(?::\d{1,2}){0,2}$/.test(timecode)) return null;

    const parts = timecode.split(':').map(Number);
    if (parts.some((part) => !Number.isInteger(part) || part < 0)) return null;

    let seconds;
    if (parts.length === 1) {
      [seconds] = parts;
    } else if (parts.length === 2) {
      if (parts[1] >= 60) return null;
      seconds = parts[0] * 60 + parts[1];
    } else {
      if (parts[1] >= 60 || parts[2] >= 60) return null;
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    return seconds <= 43200 ? seconds : null;
  }

  observeVisibility() {
    if (!window.IntersectionObserver) {
      this.isIntersecting = true;
      this.visibilityRatio = 1;
      if (this.animationEnabled) this.requestTick();
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.isIntersecting = Boolean(entry?.isIntersecting);
        this.visibilityRatio = entry?.intersectionRatio || 0;

        if (!this.isIntersecting) {
          this.stableFrames = 0;
          this.targetProgress = this.animationEnabled ? this.calculateProgress() : 1;
          this.requestTick();
          this.autoStarted = false;
          if (this.pauseOffscreen) this.pauseVideo();
          return;
        }

        if (this.animationEnabled) {
          this.stableFrames = 0;
          this.targetProgress = this.calculateProgress();
          this.requestTick();
        }

        if (
          this.autoplayEnabled &&
          this.expansionComplete &&
          this.autoplayVisibilityReached &&
          !this.autoStarted
        ) {
          this.autoStarted = true;
          this.loadAndPlay();
        }
      },
      { rootMargin: '0px', threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );

    this.visibilityObserver.observe(this.stage);
  }

  calculateProgress() {
    if (!this.stage) return 1;

    const rect = this.stage.getBoundingClientRect();
    const center = rect.top + (this.animationReferenceHeight || rect.height) / 2;
    const start = window.innerHeight * this.animationStart;
    const end = window.innerHeight * this.animationEnd;

    const progress = this.clamp((start - center) / (start - end));

    return this.smoothstep(0, this.completionProgress, progress);
  }

  calculateAnimationReferenceHeight() {
    const fullWidth = this.clientWidth;
    if (!fullWidth) return this.stage?.getBoundingClientRect().height || 0;

    const isMobile = this.mobileQuery.matches;
    const expandedHeight = Number(
      isMobile ? this.dataset.mobileHeight || 680 : this.dataset.desktopHeight || 620
    );
    const collapsedPercent = Number(
      isMobile ? this.dataset.mobileCollapsedWidth || 84 : this.dataset.desktopCollapsedWidth || 82
    );
    const aspectRatio = Number(this.dataset.aspectRatio || 16 / 9);
    const outerMargin = isMobile ? 40 : 48;
    const collapsedWidth = Math.min(
      fullWidth * (collapsedPercent / 100),
      this.maxInnerWidth || fullWidth
    );
    const collapsedHeight = collapsedWidth / aspectRatio;

    return Math.max(expandedHeight, collapsedHeight + outerMargin * 2);
  }

  requestTick() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(this.tick);
  }

  tick = (time) => {
    this.frame = null;
    const elapsed = this.lastFrameTime == null ? 16 : Math.min(64, time - this.lastFrameTime);
    this.lastFrameTime = time;

    if (this.animationEnabled && this.isIntersecting) {
      const measuredProgress = this.calculateProgress();

      if (Math.abs(measuredProgress - this.targetProgress) > 0.0005) {
        this.stableFrames = 0;
      } else {
        this.stableFrames += 1;
      }

      this.targetProgress = measuredProgress;
    }

    const difference = this.targetProgress - this.currentProgress;
    const isSettled = Math.abs(difference) < 0.001;

    if (isSettled) {
      this.currentProgress = this.targetProgress;
    } else {
      const smoothing = 1 - Math.exp((-elapsed * 5) / this.scrubDuration);
      this.currentProgress += difference * smoothing;
    }

    this.applyLayout(this.currentProgress);

    if ((this.animationEnabled && this.isIntersecting && this.stableFrames < 12) || !isSettled) {
      this.requestTick();
    } else {
      this.lastFrameTime = null;
    }
  };

  refreshLayout({ syncProgress = false } = {}) {
    this.maxInnerWidth = this.guide?.getBoundingClientRect().width || this.clientWidth;
    this.animationReferenceHeight = this.calculateAnimationReferenceHeight();

    if (syncProgress) {
      this.targetProgress = this.animationEnabled ? this.calculateProgress() : 1;
      this.currentProgress = this.targetProgress;
    }

    this.applyLayout(this.currentProgress);
  }

  applyLayout(progress) {
    const fullWidth = this.clientWidth;
    if (!fullWidth) return;

    const isMobile = this.mobileQuery.matches;
    const expandedHeight = Number(
      isMobile ? this.dataset.mobileHeight || 680 : this.dataset.desktopHeight || 620
    );
    const collapsedPercent = Number(
      isMobile ? this.dataset.mobileCollapsedWidth || 84 : this.dataset.desktopCollapsedWidth || 82
    );
    const panelRadius = Number(this.dataset.panelRadius || 24);
    const finalMediaRadius = Number(this.dataset.mediaRadius || 8);
    const mediaFraction = Number(this.dataset.mediaWidth || 55) / 100;
    const aspectRatio = Number(this.dataset.aspectRatio || 16 / 9);
    const outerMargin = isMobile ? 40 : 48;
    const maxInnerWidth = this.maxInnerWidth || fullWidth;
    const collapsedWidth = Math.min(fullWidth * (collapsedPercent / 100), maxInnerWidth);
    const collapsedHeight = collapsedWidth / aspectRatio;
    const collapsedStageHeight = Math.max(expandedHeight, collapsedHeight + outerMargin * 2);
    const stageHeight = this.lerp(collapsedStageHeight, expandedHeight, progress);
    const shellWidth = this.lerp(collapsedWidth, fullWidth, progress);
    const shellHeight = this.lerp(collapsedHeight, expandedHeight, progress);
    const innerGutter = isMobile ? 16 : 40;
    const innerWidth = Math.max(0, Math.min(maxInnerWidth, shellWidth - innerGutter * 2));
    const innerOffset = (shellWidth - innerWidth) / 2;
    const contentOpacity = this.smoothstep(0.42, 0.82, progress);
    const contentVisible = contentOpacity > 0.05;

    let finalMediaLeft;
    let finalMediaTop;
    let finalMediaWidth;
    let finalMediaHeight;
    let detailsLeft;
    let detailsTop;
    let detailsWidth;

    if (isMobile) {
      const padding = 28;
      const contentGap = 24;
      finalMediaWidth = innerWidth;
      finalMediaHeight = Math.min(finalMediaWidth / aspectRatio, expandedHeight * 0.42);
      finalMediaLeft = innerOffset;
      detailsLeft = innerOffset;
      detailsWidth = innerWidth;

      if (this.dataset.mobileOrder === 'content-first') {
        detailsTop = padding;
        finalMediaTop = expandedHeight - padding - finalMediaHeight;
      } else {
        finalMediaTop = padding;
        detailsTop = finalMediaTop + finalMediaHeight + contentGap;
      }
    } else {
      const padding = 48;
      const contentGap = Math.max(32, innerWidth * 0.04);
      finalMediaWidth = Math.min(innerWidth * mediaFraction, (expandedHeight - padding * 2) * aspectRatio);
      finalMediaHeight = finalMediaWidth / aspectRatio;
      finalMediaTop = (expandedHeight - finalMediaHeight) / 2;

      if (this.dataset.contentPosition === 'right') {
        finalMediaLeft = innerOffset;
        detailsLeft = finalMediaLeft + finalMediaWidth + contentGap;
      } else {
        finalMediaLeft = innerOffset + innerWidth - finalMediaWidth;
        detailsLeft = innerOffset;
      }

      detailsWidth = Math.max(0, innerWidth - finalMediaWidth - contentGap);
      detailsTop = shellHeight / 2;
    }

    const mediaLeft = this.lerp(0, finalMediaLeft, progress);
    const mediaTop = this.lerp(0, finalMediaTop, progress);
    const mediaWidth = this.lerp(shellWidth, finalMediaWidth, progress);
    const mediaHeight = this.lerp(shellHeight, finalMediaHeight, progress);
    const radius = this.lerp(panelRadius, 0, progress);
    const mediaRadius = this.lerp(panelRadius, finalMediaRadius, progress);

    this.stage.style.setProperty('--real-video-stage-height', `${stageHeight}px`);
    this.shell.style.setProperty('--real-video-shell-width', `${shellWidth}px`);
    this.shell.style.setProperty('--real-video-shell-height', `${shellHeight}px`);
    this.shell.style.setProperty('--real-video-shell-radius', `${radius}px`);
    this.media.style.setProperty('--real-video-media-left', `${mediaLeft}px`);
    this.media.style.setProperty('--real-video-media-top', `${mediaTop}px`);
    this.media.style.setProperty('--real-video-media-width', `${mediaWidth}px`);
    this.media.style.setProperty('--real-video-media-height', `${mediaHeight}px`);
    this.media.style.setProperty('--real-video-media-radius', `${mediaRadius}px`);
    this.details.style.setProperty('--real-video-details-left', `${detailsLeft}px`);
    this.details.style.setProperty('--real-video-details-top', `${detailsTop}px`);
    this.details.style.setProperty('--real-video-details-width', `${detailsWidth}px`);
    this.details.style.setProperty('--real-video-details-opacity', contentOpacity.toFixed(4));
    if (this.contentVisible !== contentVisible) {
      this.contentVisible = contentVisible;
      this.details.inert = !contentVisible;
      this.details.setAttribute('aria-hidden', String(!contentVisible));
      this.classList.toggle('is-content-visible', contentVisible);
    }

    if (progress < 0.18) {
      if (this.isCollapsed !== true) {
        this.isCollapsed = true;
        this.autoStarted = false;
        this.pauseVideo();
        this.showPoster();
      }
    } else if (
      this.isIntersecting &&
      this.autoplayEnabled &&
      this.expansionComplete &&
      this.autoplayVisibilityReached &&
      !this.autoStarted
    ) {
      this.isCollapsed = false;
      this.autoStarted = true;
      this.loadAndPlay();
    } else {
      this.isCollapsed = false;
    }
  }

  loadAndPlay(startSeconds = null) {
    if (!this.player || !this.playerTemplate) return;

    let iframe = this.player.querySelector('iframe');
    const shouldSeek = Number.isFinite(startSeconds);

    if (shouldSeek) this.pendingSeek = Math.max(0, Math.floor(startSeconds));

    if (!iframe) {
      const content = this.playerTemplate.content.firstElementChild?.cloneNode(true);
      if (!(content instanceof HTMLIFrameElement)) return;

      const source = new URL(content.src);
      source.searchParams.set('origin', window.location.origin);

      if (shouldSeek) {
        source.searchParams.set('start', String(this.pendingSeek));
      }

      content.src = source.toString();

      content.addEventListener('load', this.handlePlayerLoad, { once: true });

      this.player.appendChild(content);
      iframe = content;
    } else {
      if (shouldSeek && this.playerReady) {
        this.sendYouTubeCommand('seekTo', [this.pendingSeek, true]);
        this.pendingSeek = null;
      }
      this.sendYouTubeCommand('playVideo');
    }

    this.classList.add('is-playing');
  }

  handlePlayerLoad = () => {
    this.playerReady = true;

    if (Number.isFinite(this.pendingSeek)) {
      this.sendYouTubeCommand('seekTo', [this.pendingSeek, true]);
      this.pendingSeek = null;
    }

    this.sendYouTubeCommand('playVideo');
  };

  pauseVideo() {
    this.sendYouTubeCommand('pauseVideo');
  }

  showPoster() {
    this.classList.remove('is-playing');
  }

  sendYouTubeCommand(command, args = []) {
    const iframe = this.player?.querySelector('iframe');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    iframe.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: command, args }),
      'https://www.youtube-nocookie.com'
    );
  }

  addMediaQueryListener(query, handler) {
    if (query.addEventListener) {
      query.addEventListener('change', handler);
    } else {
      query.addListener(handler);
    }
  }

  removeMediaQueryListener(query, handler) {
    if (!query) return;

    if (query.removeEventListener) {
      query.removeEventListener('change', handler);
    } else {
      query.removeListener(handler);
    }
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  smoothstep(start, end, value) {
    const progress = this.clamp((value - start) / (end - start));
    return progress * progress * (3 - 2 * progress);
  }

  clamp(value) {
    return Math.min(1, Math.max(0, value));
  }
}

if (!customElements.get('real-video-feature')) {
  customElements.define('real-video-feature', RealVideoFeature);
}
